import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Leaf, Plus, Trash2, ShieldCheck, Volume2, Edit2, Sparkles } from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common supplements with suggestions
const SUPPLEMENT_SUGGESTIONS = {
  vitamins: [
    { name: 'Vitamin D3', dosage: '1000 IU', hi: 'विटामिन D3' },
    { name: 'Vitamin B12', dosage: '1000 mcg', hi: 'विटामिन B12' },
    { name: 'Vitamin C', dosage: '500 mg', hi: 'विटामिन C' },
    { name: 'Vitamin E', dosage: '400 IU', hi: 'विटामिन E' },
    { name: 'Vitamin B Complex', dosage: '1 tablet', hi: 'विटामिन B कॉम्प्लेक्स' },
    { name: 'Folic Acid', dosage: '400 mcg', hi: 'फोलिक एसिड' },
  ],
  minerals: [
    { name: 'Iron', dosage: '65 mg', hi: 'आयरन' },
    { name: 'Calcium', dosage: '500 mg', hi: 'कैल्शियम' },
    { name: 'Zinc', dosage: '15 mg', hi: 'जिंक' },
    { name: 'Magnesium', dosage: '400 mg', hi: 'मैग्नीशियम' },
  ],
  ayurvedic: [
    { name: 'Ashwagandha', dosage: '500 mg', hi: 'अश्वगंधा' },
    { name: 'Triphala', dosage: '500 mg', hi: 'त्रिफला' },
    { name: 'Brahmi', dosage: '300 mg', hi: 'ब्राह्मी' },
    { name: 'Tulsi', dosage: '500 mg', hi: 'तुलसी' },
    { name: 'Giloy', dosage: '500 mg', hi: 'गिलोय' },
    { name: 'Shatavari', dosage: '500 mg', hi: 'शतावरी' },
    { name: 'Amla', dosage: '500 mg', hi: 'आंवला' },
    { name: 'Chyawanprash', dosage: '1 spoon', hi: 'च्यवनप्राश' },
  ],
  herbal: [
    { name: 'Omega-3 Fish Oil', dosage: '1000 mg', hi: 'ओमेगा-3' },
    { name: 'Turmeric/Curcumin', dosage: '500 mg', hi: 'हल्दी' },
    { name: 'Ginger Extract', dosage: '250 mg', hi: 'अदरक' },
    { name: 'Garlic Extract', dosage: '600 mg', hi: 'लहसुन' },
    { name: 'Green Tea Extract', dosage: '400 mg', hi: 'ग्रीन टी' },
  ],
};

const SUPPLEMENT_TYPES = [
  { value: 'vitamin', label: 'Vitamin', hi: 'विटामिन' },
  { value: 'mineral', label: 'Mineral', hi: 'खनिज' },
  { value: 'ayurvedic', label: 'Ayurvedic', hi: 'आयुर्वेदिक' },
  { value: 'herbal', label: 'Herbal', hi: 'हर्बल' },
  { value: 'other', label: 'Other', hi: 'अन्य' },
];

const TIMING_OPTIONS = [
  { value: 'morning', label: 'Morning', hi: 'सुबह' },
  { value: 'afternoon', label: 'Afternoon', hi: 'दोपहर' },
  { value: 'evening', label: 'Evening', hi: 'शाम' },
  { value: 'night', label: 'Night', hi: 'रात' },
  { value: 'with_food', label: 'With Food', hi: 'भोजन के साथ' },
  { value: 'empty_stomach', label: 'Empty Stomach', hi: 'खाली पेट' },
];

const FREQUENCY_OPTIONS = [
  { value: 'once_daily', label: 'Once Daily', hi: 'दिन में एक बार' },
  { value: 'twice_daily', label: 'Twice Daily', hi: 'दिन में दो बार' },
  { value: 'thrice_daily', label: 'Thrice Daily', hi: 'दिन में तीन बार' },
  { value: 'weekly', label: 'Once Weekly', hi: 'सप्ताह में एक बार' },
  { value: 'as_needed', label: 'As Needed', hi: 'जरूरत के अनुसार' },
];

const SupplementsPage = () => {
  const { t, language, speak } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'vitamin',
    dosage: '',
    frequency: 'once_daily',
    timing: 'morning',
    start_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/health/supplements`);
      setEntries(res.data);
    } catch (error) {
      toast.error('Failed to load supplements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/health/supplements/${editingEntry.id}`, formData);
        toast.success(language === 'hi' ? 'अपडेट हो गया!' : 'Supplement updated!');
      } else {
        await axios.post(`${API_URL}/health/supplements`, formData);
        toast.success(language === 'hi' ? 'सप्लीमेंट जोड़ा गया!' : 'Supplement added!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(editingEntry ? 'Failed to update' : 'Failed to add supplement');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'vitamin',
      dosage: '',
      frequency: 'once_daily',
      timing: 'morning',
      start_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingEntry(null);
    setShowSuggestions(false);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      name: entry.name || '',
      type: entry.type || 'vitamin',
      dosage: entry.dosage || '',
      frequency: entry.frequency || 'once_daily',
      timing: entry.timing || 'morning',
      start_date: entry.start_date || new Date().toISOString().split('T')[0],
      notes: entry.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await axios.delete(`${API_URL}/health/supplements/${deleteConfirmId}`);
      toast.success(language === 'hi' ? 'हटा दिया गया' : 'Deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const selectSuggestion = (supp) => {
    setFormData({
      ...formData,
      name: supp.name,
      dosage: supp.dosage
    });
    setShowSuggestions(false);
  };

  const getTypeLabel = (type) => {
    const found = SUPPLEMENT_TYPES.find(t => t.value === type);
    return found ? (language === 'hi' ? found.hi : found.label) : type;
  };

  const getTimingLabel = (timing) => {
    const found = TIMING_OPTIONS.find(t => t.value === timing);
    return found ? (language === 'hi' ? found.hi : found.label) : timing;
  };

  const getFrequencyLabel = (freq) => {
    const found = FREQUENCY_OPTIONS.find(f => f.value === freq);
    return found ? (language === 'hi' ? found.hi : found.label) : freq;
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'vitamin': return 'bg-orange-100 text-orange-700';
      case 'mineral': return 'bg-blue-100 text-blue-700';
      case 'ayurvedic': return 'bg-green-100 text-green-700';
      case 'herbal': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Get suggestions based on type
  const getSuggestionsForType = (type) => {
    switch(type) {
      case 'vitamin': return SUPPLEMENT_SUGGESTIONS.vitamins;
      case 'mineral': return SUPPLEMENT_SUGGESTIONS.minerals;
      case 'ayurvedic': return SUPPLEMENT_SUGGESTIONS.ayurvedic;
      case 'herbal': return SUPPLEMENT_SUGGESTIONS.herbal;
      default: return [];
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-green-100">
              <Leaf className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">
                {language === 'hi' ? 'सप्लीमेंट्स' : 'Supplements'}
              </h1>
              <p className="text-slate-500">{entries.length} {language === 'hi' ? 'आइटम' : 'items'}</p>
            </div>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2340]" data-testid="add-supplement-btn">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'जोड़ें' : 'Add'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingEntry 
                    ? (language === 'hi' ? 'सप्लीमेंट संपादित करें' : 'Edit Supplement')
                    : (language === 'hi' ? 'नया सप्लीमेंट' : 'Add Supplement')
                  }
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Selection */}
                <div>
                  <Label>{language === 'hi' ? 'प्रकार' : 'Type'}</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                    <SelectTrigger data-testid="type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPLEMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {language === 'hi' ? type.hi : type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick Suggestions */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    {language === 'hi' ? 'सुझाव' : 'Quick Pick'}
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getSuggestionsForType(formData.type).slice(0, 6).map((supp, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-green-50"
                        onClick={() => selectSuggestion(supp)}
                      >
                        {language === 'hi' ? supp.hi : supp.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <Label>{language === 'hi' ? 'नाम' : 'Name'} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={language === 'hi' ? 'जैसे: Vitamin D3' : 'e.g., Vitamin D3'}
                    required
                    data-testid="supplement-name-input"
                  />
                </div>

                {/* Dosage */}
                <div>
                  <Label>{language === 'hi' ? 'खुराक' : 'Dosage'}</Label>
                  <Input
                    value={formData.dosage}
                    onChange={(e) => setFormData({...formData, dosage: e.target.value})}
                    placeholder={language === 'hi' ? 'जैसे: 1000 IU' : 'e.g., 1000 IU, 500mg'}
                    data-testid="dosage-input"
                  />
                </div>

                {/* Frequency */}
                <div>
                  <Label>{language === 'hi' ? 'कितनी बार' : 'Frequency'}</Label>
                  <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                    <SelectTrigger data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map(freq => (
                        <SelectItem key={freq.value} value={freq.value}>
                          {language === 'hi' ? freq.hi : freq.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Timing */}
                <div>
                  <Label>{language === 'hi' ? 'समय' : 'When to take'}</Label>
                  <Select value={formData.timing} onValueChange={(v) => setFormData({...formData, timing: v})}>
                    <SelectTrigger data-testid="timing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMING_OPTIONS.map(time => (
                        <SelectItem key={time.value} value={time.value}>
                          {language === 'hi' ? time.hi : time.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div>
                  <Label>{language === 'hi' ? 'शुरू की तारीख' : 'Start Date'}</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="start-date-input"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>{language === 'hi' ? 'नोट्स' : 'Notes'}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder={language === 'hi' ? 'कोई विशेष जानकारी...' : 'Any special instructions...'}
                    data-testid="notes-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">
                    {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-supplement-btn">
                    {editingEntry 
                      ? (language === 'hi' ? 'अपडेट करें' : 'Update')
                      : (language === 'hi' ? 'सेव करें' : 'Save')
                    }
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <Leaf className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {language === 'hi' ? 'कोई सप्लीमेंट नहीं जोड़ा गया' : 'No supplements added yet'}
              </p>
              <p className="text-sm text-slate-400 mt-2">
                {language === 'hi' ? 'विटामिन, मिनरल और आयुर्वेदिक दवाइयां यहां जोड़ें' : 'Add vitamins, minerals, and ayurvedic supplements here'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className="card-interactive" data-testid={`supplement-card-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getTypeColor(entry.type)}>
                          {getTypeLabel(entry.type)}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-lg text-[#0D2340]">{entry.name}</h3>
                      <p className="text-slate-600">
                        {entry.dosage && <span>{entry.dosage} • </span>}
                        {getFrequencyLabel(entry.frequency)}
                        {entry.timing && <span> • {getTimingLabel(entry.timing)}</span>}
                      </p>
                      {entry.notes && (
                        <p className="text-sm text-slate-500 mt-1">{entry.notes}</p>
                      )}
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {language === 'hi' ? 'शुरू:' : 'Started:'} {entry.start_date}
                        </span>
                        <Badge className="blockchain-badge text-xs">
                          <ShieldCheck className="h-3 w-3" />
                          {entry.blockchain_hash?.slice(0, 8)}...
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(entry)}
                        className="text-blue-500 hover:text-blue-700"
                        data-testid={`edit-supplement-${entry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speak(`${entry.name}. ${entry.dosage}, ${getFrequencyLabel(entry.frequency)}`)}
                        data-testid={`speak-supplement-${entry.id}`}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-supplement-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'hi' ? 'क्या आप वाकई हटाना चाहते हैं?' : 'Delete this supplement?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-sm">
            {language === 'hi' ? 'यह क्रिया पूर्ववत नहीं की जा सकती।' : 'This action cannot be undone.'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
              {language === 'hi' ? 'हटाएं' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SupplementsPage;
